/**
 * Document-branding preview for tenant Settings (§05 "Förhandsgranskning").
 *
 * Shows what a *rendered* contract looks like with the tenant's branding.
 * To stay truthful — and to never drift from the real output again
 * (REGTA-54) — the letterhead and footer are the SAME atoms the server-side
 * render uses (`TemplateLetterhead` / `TemplateFooter`), fed the effective
 * branding through `OwnerBrandingProvider`: owner mark top-left (the owner
 * default, falling back to the owner name), tenant mark top-right (the tenant
 * override, invisible when unset). The body/signature rows are decorative mock.
 */

import {
  OwnerBrandingProvider,
  TemplateLetterhead,
  TemplateFooter,
  type OwnerBrandingValue,
} from '@hyranai/regta-templates';

const SERIF = '"Georgia", "Times New Roman", serif';

const SAMPLE_TITLE = 'Hyresavtal — bostadslägenhet';
const SAMPLE_FORM_NUMBER = 'Formulär 1';

export function LetterheadPreview({
  ownerLogo,
  ownerName,
  tenantLogo,
  tenantName,
}: {
  ownerLogo: string | null;
  ownerName: string;
  tenantLogo: string | null;
  tenantName: string | null;
}) {
  // Effective branding, exactly as the renderer receives it.
  const branding: OwnerBrandingValue = {
    logoUrl: ownerLogo,
    name: ownerName,
    tenantLogoUrl: tenantLogo,
    tenantName,
  };

  return (
    <div className="border border-stone-200 bg-stone-100 p-6">
      <div className="text-[9px] uppercase tracking-widest text-stone-400">
        A4 · sample contract
      </div>
      <div className="mt-3 aspect-[1/1.414] w-full max-w-md bg-white p-8 shadow-sm">
        {/* Real letterhead — owner mark left (default), tenant mark right (override). */}
        <OwnerBrandingProvider value={branding}>
          <TemplateLetterhead title={SAMPLE_TITLE} formNumber={SAMPLE_FORM_NUMBER} />
        </OwnerBrandingProvider>

        <div className="mt-3 h-px bg-stone-300" aria-hidden />
        <div className="mt-1 h-px bg-stone-200" aria-hidden />

        {/* Sample text */}
        <div className="mt-6 space-y-2.5">
          <div
            className="text-[18px] leading-tight text-stone-900"
            style={{ fontFamily: SERIF, fontWeight: 500 }}
          >
            {SAMPLE_TITLE}
          </div>
          <div className="text-[9px] text-stone-500">
            Mellan {tenantName ?? 'er organisation'} och Anna Andersson
          </div>
        </div>

        <div className="mt-5 space-y-1.5">
          {[100, 96, 88, 92, 70].map((w, i) => (
            <div
              key={i}
              className="h-[3px] rounded-sm bg-stone-200"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>

        <div className="mt-5 space-y-1.5">
          {[100, 100, 84].map((w, i) => (
            <div
              key={i}
              className="h-[3px] rounded-sm bg-stone-200"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>

        {/* Signature blocks */}
        <div className="mt-8 grid grid-cols-2 gap-6">
          {['Hyresvärd', 'Hyresgäst'].map((role) => (
            <div key={role}>
              <div className="h-px bg-stone-900 opacity-60" />
              <div className="mt-1 text-[8px] uppercase tracking-widest text-stone-400">
                {role}
              </div>
            </div>
          ))}
        </div>

        {/* Real document footer (form number + Regta). */}
        <div className="mt-8 border-t border-stone-200 pt-3">
          <TemplateFooter formNumber={SAMPLE_FORM_NUMBER} />
        </div>
      </div>
    </div>
  );
}
